import { useEffect, useRef } from 'react'
import type { InternalStatus, StatusId } from '@ludus/core'
import { usePipelineShowsValue, usePipelineWords } from '../../data/usePipelinesSource.ts'
import type { StageMetrics } from '../../types.ts'
import type { StageKind } from '@/shared/utils/stageColors.ts'
import { StageCard } from './StageCard.tsx'

interface StageDashboardProps {
  stages: InternalStatus[]
  stageKinds: Record<string, StageKind>
  metricsByStage: Map<StatusId, StageMetrics>
  selectedStageId: StatusId | null
  onSelect: (id: StatusId) => void
}

/**
 * Bar-chart-style stage row.
 *
 * Layout: a CSS grid with `grid-auto-columns: minmax(96px, 1fr)`. Cards expand to fill
 * the row when there's space, collapse to 96px and overflow-scroll when there isn't.
 * Each card is a vertical track with a coloured bar (bottom-aligned, height proportional
 * to `totalValue` share) and a two-line label below, so all bars share a common baseline.
 */
export function StageDashboard({
  stages,
  stageKinds,
  metricsByStage,
  selectedStageId,
  onSelect,
}: StageDashboardProps) {
  const words = usePipelineWords()
  const showValue = usePipelineShowsValue()
  const selectedRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    selectedRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    })
  }, [selectedStageId])

  let maxValue = 0
  metricsByStage.forEach((m) => {
    if (m.totalValue > maxValue) maxValue = m.totalValue
  })

  const moveSelection = (delta: 1 | -1) => {
    const idx = stages.findIndex((s) => s.id === selectedStageId)
    const nextIdx = Math.max(0, Math.min(stages.length - 1, idx + delta))
    const next = stages[nextIdx]
    if (next && next.id !== selectedStageId) onSelect(next.id)
  }

  return (
    <section
      aria-label="Pipeline stages"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault()
          moveSelection(-1)
        } else if (e.key === 'ArrowRight') {
          e.preventDefault()
          moveSelection(1)
        }
      }}
      className="rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
    >
      <div className="overflow-x-auto pb-2">
        <div
          className="grid h-[120px] items-stretch gap-2 sm:h-[148px]"
          style={{
            gridAutoFlow: 'column',
            gridAutoColumns: 'minmax(96px, 1fr)',
          }}
        >
          {stages.map((stage, idx) => {
            const metrics = metricsByStage.get(stage.id)
            if (!metrics) return null
            const selected = stage.id === selectedStageId
            const valueShare = maxValue > 0 ? metrics.totalValue / maxValue : 0
            return (
              <div key={stage.id} ref={selected ? selectedRef : null} className="h-full">
                <StageCard
                  index={idx}
                  totalStages={stages.length}
                  stage={stage}
                  metrics={metrics}
                  selected={selected}
                  vivid={selectedStageId === null}
                  valueShare={valueShare}
                  showValue={showValue}
                  countLabel={words('pages.pipelines.stage.deals', {
                    defaultValue: '{{count}} deals',
                    count: metrics.leadCount,
                  })}
                  kind={stageKinds[stage.id]}
                  onClick={() => onSelect(stage.id)}
                />
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
